import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { UrlService } from 'app/core/http/url.service';
import { IGameShowFeedbackType } from 'app/student/game-show/types/IGameshowFeedbackType';
import { IGameQuestion } from 'app/student/models/game-question';
import { LaunchGameShowRequest } from 'app/teacher/game-show/models/launch-game-show-request.model';
import { LaunchGameShowResponse } from 'app/teacher/game-show/models/launch-game-show-response.model';
import { GameShowQuestionModel } from 'app/teacher/models/game-show-question.model';
import { GameShowActivityModel } from 'app/teacher/models/gameshow-activity.model';
import { GameShowState } from '../models/game-show-state.model';
import { IGameShowRunSettings } from '../models/gameshow-run-settings';

@Injectable({
  providedIn: 'root'
})
export class GameShowDataService {
  private readonly httpClient = inject(HttpClient);
  private readonly urlService = inject(UrlService);

  public launchGameShow(publicUnitId: string, request: LaunchGameShowRequest): Observable<LaunchGameShowResponse> {
    const url = this.urlService.buildServerApiUrl('teacher', 'gameShow', publicUnitId, 'launch');

    return this.httpClient.post<LaunchGameShowResponse>(url, request);
  }

  public getQuestionContentTeacher(questionId: string, activityId?: string): Observable<GameShowQuestionModel> {
    const url = this.urlService.buildServerApiUrl('teacher', 'QuestionContent', questionId);

    return this.httpClient.get<GameShowQuestionModel>(url, { params: activityId ? { activityId } : {} });
  }

  public getQuestionContentStudent(questionId: string, activityId?: string): Observable<GameShowQuestionModel> {
    const url = this.urlService.buildServerApiUrl('student', 'QuestionContent', questionId);

    return this.httpClient.get<GameShowQuestionModel>(url, { params: activityId ? { activityId } : {} });
  }

  public getGameShow(activityId: string): Observable<GameShowState> {
    const url = this.urlService.buildServerApiUrl('student', 'game-show', activityId);
    return this.httpClient.get<GameShowState>(url);
  }

  public getSurveyFeedbackStudent(activityId: string): Observable<IGameShowFeedbackType[]> {
    const url = this.urlService.buildServerApiUrl('student', 'game-show', activityId, 'feedback-types');
    return this.httpClient.get<IGameShowFeedbackType[]>(url);
  }

  public sendFeedbackTeacher(activityId: string, score: number, comments: string): Observable<void> {
    const url = this.urlService.buildServerApiUrl('teacher', 'game-show', activityId, 'feedback');
    return this.httpClient.post<void>(url, { score, comments });
  }

  public getGameShowSettings(classId: string, publicUnitId: string): Observable<IGameShowRunSettings> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowSettings', classId, publicUnitId);

    return this.httpClient.get<IGameShowRunSettings>(url);
  }

  public getIsGameShowVerboseAnalytics(classId: string, isTeacher: boolean): Observable<boolean> {
    const url = isTeacher
      ? this.urlService.buildServerApiUrl('teacher', 'gameShowRun', classId, 'isGameShowVerboseAnalytics')
      : this.urlService.buildServerApiUrl('student', 'gameShow', classId, 'isGameShowVerboseAnalytics');

    return this.httpClient.get<boolean>(url);
  }

  public closeGameShow(activityId: string): Observable<unknown> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', activityId, 'Close');

    return this.httpClient.post<unknown>(url, {});
  }

  public getGameShowActivityInformation(activityId: string): Observable<GameShowActivityModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/GameShowRun', activityId, 'Activity');

    return this.httpClient.get<GameShowActivityModel>(url);
  }

  public teacherLikeSurvey(unitId: string): Observable<void> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'Unit', unitId, 'Like');
    return this.httpClient.put<void>(url, {});
  }

  public teacherUnlikeSurvey(unitId: string): Observable<void> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'Unit', unitId, 'Like');
    return this.httpClient.delete<void>(url);
  }

  public convertToGameshowQuestion(question: GameShowQuestionModel): IGameQuestion {
    return { ...question,
      AttemptItemOrder: 1,
      DisplayUploadFocus: false,
      GoalId: '',
      IsAnswerInline: false,
      SubmittedChoices: [],
      NumAnswers: 0,
      VideoId: '',
      IsSolutionSubmitted: false,
      AttemptNumber: 1,
      NumberOverride: '',
      ContentSlides: { Before: [], After: [] }
    };
  }
}
